import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { FeedbackPriority, FeedbackStatus, FeedbackType } from '@crm/shared'
import { deleteFeedback, listFeedback, updateFeedbackStatus } from './feedback.api'
import { Loader } from '../../shared/ui/Loader'
import { FeedbackCreateModal } from './FeedbackCreateModal'
import { FeedbackDetailModal } from './FeedbackDetailModal'
import { PRIORITY_LABELS, STATUS_COLORS, STATUS_LABELS, TYPE_LABELS } from './feedback.labels'
import { useAuthStore } from '../auth/authStore'

export function FeedbackPage() {
  const role = useAuthStore((s) => s.user?.role)
  const isAdmin = role === 'ADMIN'
  const qc = useQueryClient()

  const [creating, setCreating] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null)
  const [type, setType] = useState<FeedbackType | ''>('')
  const [priority, setPriority] = useState<FeedbackPriority | ''>('')
  const [status, setStatus] = useState<FeedbackStatus | ''>('')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [fetchingAll, setFetchingAll] = useState(false)
  const limit = 20

  const { data, isLoading } = useQuery({
    queryKey: ['feedback', { type, priority, status, page }],
    queryFn: () =>
      listFeedback({
        type: type || undefined,
        priority: priority || undefined,
        status: status || undefined,
        page,
        limit,
      }),
    staleTime: 30_000,
  })

  const totalPages = data ? Math.max(1, Math.ceil(data.total / limit)) : 1
  const items = data?.items ?? []
  const allIds = useMemo(() => items.map((i) => i.id), [items])
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id))

  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleAll() {
    setSelected((prev) => {
      if (allIds.every((id) => prev.has(id))) {
        const next = new Set(prev)
        for (const id of allIds) next.delete(id)
        return next
      }
      const next = new Set(prev)
      for (const id of allIds) next.add(id)
      return next
    })
  }

  async function selectAllPages() {
    if (!data || fetchingAll) return
    setFetchingAll(true)
    try {
      const all = await listFeedback({
        type: type || undefined,
        priority: priority || undefined,
        status: status || undefined,
        limit: data.total,
        page: 1,
      })
      setSelected(new Set(all.items.map((i) => i.id)))
    } finally {
      setFetchingAll(false)
    }
  }

  const bulkDelete = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map((id) => deleteFeedback(id)))
    },
    onSuccess: () => {
      setSelected(new Set())
      void qc.invalidateQueries({ queryKey: ['feedback'] })
    },
  })

  const bulkStatus = useMutation({
    mutationFn: async ({ ids, s }: { ids: string[]; s: FeedbackStatus }) => {
      await Promise.all(ids.map((id) => updateFeedbackStatus(id, s)))
    },
    onSuccess: () => {
      setSelected(new Set())
      void qc.invalidateQueries({ queryKey: ['feedback'] })
    },
  })

  const selectedCount = selected.size
  const bulkBusy = bulkDelete.isPending || bulkStatus.isPending

  return (
    <div className="max-w-5xl mx-auto space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-xl font-semibold">Обратная связь</h1>
        <button
          onClick={() => setCreating(true)}
          className="px-4 py-1.5 text-sm bg-brand-600 text-white rounded hover:bg-brand-700"
        >
          + Новая запись
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap bg-white p-3 rounded border">
        <select
          value={type}
          onChange={(e) => {
            setType(e.target.value as FeedbackType | '')
            setPage(1)
          }}
          className="border rounded px-2 py-1 text-sm"
        >
          <option value="">Все типы</option>
          <option value="BUG">🐛 Баги</option>
          <option value="IDEA">💡 Идеи</option>
        </select>
        <select
          value={priority}
          onChange={(e) => {
            setPriority(e.target.value as FeedbackPriority | '')
            setPage(1)
          }}
          className="border rounded px-2 py-1 text-sm"
        >
          <option value="">Любой приоритет</option>
          <option value="CRITICAL">Критично</option>
          <option value="NORMAL">Обычный</option>
          <option value="LOW">Низкий</option>
        </select>
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as FeedbackStatus | '')
            setPage(1)
          }}
          className="border rounded px-2 py-1 text-sm"
        >
          <option value="">Любой статус</option>
          <option value="NEW">Новая</option>
          <option value="IN_PROGRESS">В работе</option>
          <option value="FIXED">Исправлено</option>
          <option value="REJECTED">Отклонено</option>
        </select>
      </div>

      {isAdmin && items.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap bg-gray-50 border rounded px-3 py-2 text-sm">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" checked={allSelected} onChange={toggleAll} className="h-4 w-4" />
            <span className="text-gray-700">
              Выбрать всё на странице {selectedCount > 0 && `(выбрано: ${selectedCount})`}
            </span>
          </label>
          {allSelected && data && data.total > items.length && (
            <button
              onClick={() => void selectAllPages()}
              disabled={fetchingAll}
              className="text-brand-600 hover:underline disabled:text-gray-400 flex items-center gap-1"
            >
              {fetchingAll ? (
                <>
                  <Loader variant="bus" size={14} /> Загрузка…
                </>
              ) : (
                `Выбрать все ${data.total} записей`
              )}
            </button>
          )}
          {selectedCount > 0 && (
            <div className="flex items-center gap-2 ml-auto">
              <select
                disabled={bulkBusy}
                defaultValue=""
                onChange={(e) => {
                  const s = e.target.value as FeedbackStatus
                  if (!s) return
                  bulkStatus.mutate({ ids: [...selected], s })
                  e.target.value = ''
                }}
                className="border rounded px-2 py-1 text-sm bg-white"
              >
                <option value="">Сменить статус…</option>
                <option value="NEW">Новая</option>
                <option value="IN_PROGRESS">В работе</option>
                <option value="FIXED">Исправлено</option>
                <option value="REJECTED">Отклонено</option>
              </select>
              <button
                disabled={bulkBusy}
                onClick={() => {
                  if (window.confirm(`Удалить записей: ${selectedCount}?`)) {
                    bulkDelete.mutate([...selected])
                  }
                }}
                className="px-3 py-1 text-sm text-red-600 border border-red-200 rounded hover:bg-red-50 disabled:opacity-50"
              >
                Удалить ({selectedCount})
              </button>
              <button
                disabled={bulkBusy}
                onClick={() => setSelected(new Set())}
                className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50"
              >
                Сброс
              </button>
            </div>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="text-gray-500">Загрузка…</div>
      ) : items.length === 0 ? (
        <div className="text-gray-500 text-center py-8">Пока нет записей</div>
      ) : (
        <div className="space-y-2">
          {items.map((fb) => {
            const isChecked = selected.has(fb.id)
            return (
              <div
                key={fb.id}
                onClick={() => setOpenId(fb.id)}
                className={`bg-white border rounded p-3 hover:border-brand-500 transition-colors cursor-pointer ${
                  isChecked ? 'border-brand-500 bg-brand-50/40' : ''
                }`}
              >
                <div className="flex items-center gap-2 flex-wrap text-xs">
                  {isAdmin && (
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleOne(fb.id)}
                      className="h-4 w-4"
                      aria-label="Выбрать запись"
                    />
                  )}
                  <span className="px-2 py-0.5 rounded bg-gray-100">{TYPE_LABELS[fb.type]}</span>
                  <span className="px-2 py-0.5 rounded bg-gray-100">
                    {PRIORITY_LABELS[fb.priority]}
                  </span>
                  <span className={`px-2 py-0.5 rounded ${STATUS_COLORS[fb.status]}`}>
                    {STATUS_LABELS[fb.status]}
                  </span>
                  {fb.screenshot && <span className="text-gray-400">📎</span>}
                  <span className="ml-auto text-gray-400">
                    {new Date(fb.createdAt).toLocaleDateString('ru-RU')}
                  </span>
                </div>
                <div className="mt-1 font-medium text-sm">{fb.title}</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {fb.author?.name ?? fb.author?.email ?? '—'}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {data && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1 text-sm border rounded disabled:opacity-50"
          >
            ← Назад
          </button>
          <span className="text-sm text-gray-500">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="px-3 py-1 text-sm border rounded disabled:opacity-50"
          >
            Вперёд →
          </button>
        </div>
      )}

      {creating && <FeedbackCreateModal onClose={() => setCreating(false)} />}
      {openId && <FeedbackDetailModal id={openId} onClose={() => setOpenId(null)} />}
    </div>
  )
}
